import { useSuspenseQueries } from '@tanstack/react-query';
import clsx from 'clsx';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';
import { ErrorBoundary } from '@gitbook/util-debug';
import { assertIsDefined } from '@gitbook/util-ts';

import {
    APIErrorBoundary,
    FeaturedIntegrations,
    INTEGRATIONS_PER_PAGE,
    useFeaturedIntegrations,
} from '../../../../Components';
import { apiQuery } from '../../../../state';
import imageGitSync from '../../assets/learn-card-git-sync.raw.svg';
import imageWebinar from '../../assets/learn-card-webinar.raw.svg';
import { useHomeContext } from '../../HomeContext';
import { HomeSection, HomeSectionBody, HomeSectionHeading } from '../HomeSection';
import { LearnGitBook } from './LearnGitBook';
import { MarketingCard } from './MarketingCard';

export function DiscoverySection() {
    return (
        <ErrorBoundary>
            <APIErrorBoundary
                fallback={{
                    403: <DiscoverySectionView featuredIntegrations={null} />,
                    404: <DiscoverySectionView featuredIntegrations={null} />,
                }}
            >
                <DiscoverySectionWithData />
            </APIErrorBoundary>
        </ErrorBoundary>
    );
}

function DiscoverySectionWithData() {
    const { organization } = useHomeContext();

    const [{ data: integrations }, { data: installedIntegrations }] = useSuspenseQueries({
        queries: [
            {
                ...apiQuery(
                    'listOrganizationIntegrations',
                    { organizationId: organization.id, limit: INTEGRATIONS_PER_PAGE },
                    { nullOnError: true }
                ),
                refetchOnMount: false,
            },
            apiQuery('listOrganizationIntegrationsStatus', {
                organizationId: organization.id,
                target: 'organization',
            }),
        ],
    });

    assertIsDefined(installedIntegrations);

    const featuredIntegrations = useFeaturedIntegrations({
        integrations: integrations?.items,
        installedIntegrations: installedIntegrations.items.map(
            (installation) => installation.integration.name
        ),
        useCase: organization.useCase,
        limit: 6,
    });

    return <DiscoverySectionView featuredIntegrations={featuredIntegrations} />;
}

function DiscoverySectionView(props: { featuredIntegrations: api.Integration[] | null }) {
    const { featuredIntegrations } = props;
    const hasFeaturedIntegrations = featuredIntegrations && featuredIntegrations.length > 0;
    const { organization } = useHomeContext();

    // TODO: move back to feature flagging using Bucket.co once they support it (very soon)
    const webinarCardProps = {
        cta: 'Watch now',
        description:
            'Watch our latest webinars to learn how to create engaging, interactive documentation with GitBook.',
        title: 'On demand webinars',
        url: 'https://www.gitbook.com/events',
    };
    const gitsyncCardProps = {
        cta: 'Read more',
        description:
            'Set up Git Sync to encourage your engineers to contribute to your docs right from GitHub or GitLab.',
        title: 'Sync your docs with Git',
        url: 'https://gitbook.com/docs/integrations/git-sync',
    };

    return (
        <>
            <LearnGitBook />

            <div className="gap-6 max-lg:grid max-lg:grid-cols-1 lg:flex lg:gap-10">
                {/* Featured integrations */}
                {hasFeaturedIntegrations ? (
                    <HomeSection className="flex-1">
                        <HomeSectionBody className="flex-col">
                            <FeaturedIntegrations
                                title={
                                    <HomeSectionHeading>
                                        Make GitBook more powerful
                                    </HomeSectionHeading>
                                }
                                kind="minimal"
                                integrations={featuredIntegrations}
                                organizationId={organization.id}
                                className="flex w-full flex-1 flex-col gap-6"
                            />

                            <Button
                                label="View all integrations"
                                leadingIcon={Icon.Integrations}
                                kind="secondary"
                                href={{ screen: { type: 'integrations' } }}
                                className="flex self-start"
                            />
                        </HomeSectionBody>
                    </HomeSection>
                ) : null}

                {/* Dive deeper - Marketing driven cards */}
                <HomeSection className="flex-1">
                    <HomeSectionHeading>Dive deeper</HomeSectionHeading>

                    <HomeSectionBody
                        className={clsx(hasFeaturedIntegrations ? 'flex-wrap' : 'max-lg:flex-wrap')}
                    >
                        <MarketingCard card={webinarCardProps} image={imageWebinar} />
                        <MarketingCard card={gitsyncCardProps} image={imageGitSync} />
                    </HomeSectionBody>
                </HomeSection>
            </div>
        </>
    );
}
